import { faBedPulse } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import images from "../../assets/images";
import CourseCard from "../../components/cards/CourseCard";
import OwlCarousel from "../../components/carousel";
import Footer from "../../components/footer";
import Header from "../../components/header";
import FitnessSection from "../../components/home/FitnessSection";
import Loader from "../../components/loader";
import { useTranslate } from "../../context/TranslationContext";
import { useGetCoursesByCategoryQuery } from "../../redux/Apis/Course";
import { useGetInstructorsQuery } from "../../redux/Apis/User";

const Profile = () => {
  const lang = useSelector((state) => state.language.lang);
  const { t } = useTranslate("instructorDetails");
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const userId = user._id;

  const {
    data: userData,
    refetch,
    isLoading: isInstructorLoading,
  } = useGetInstructorsQuery(
    { id: userId, params: { lang } },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: courseTrackData,
    refetch: refetchCourseTrack,
    isLoading: isCourseTrackLoading,
  } = useGetCoursesByCategoryQuery(
    { created_by: userId, type: "course,track", lang },
    {
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );

  const {
    data: digitalProductData,
    refetch: refetchDigitalProduct,
    isLoading: isDigitalProductLoading,
  } = useGetCoursesByCategoryQuery(
    { created_by: userId, type: "digital_product", lang },
    {
      refetchOnReconnect: true,
      refetchOnFocus: true,
    },
  );

  useEffect(() => {
    refetchCourseTrack();
    refetchDigitalProduct();
    refetch();
  }, [refetchCourseTrack, refetchDigitalProduct, refetch]);

  const bannerPreview = useMemo(() => {
    const banner = userData?.data?.user?.banner_img;

    if (!banner) return null;
    if (banner.startsWith("http")) return banner;
    if (banner.startsWith("images/")) {
      return `${process.env.REACT_APP_S3_BASE_URL}${banner}`;
    }
    return null;
  }, [userData?.data?.user?.banner_img]);

  const logoPreview = useMemo(() => {
    const logo = userData?.data?.user?.logo_img;

    if (!logo) return null;
    if (logo.startsWith("http")) return logo;
    if (logo.startsWith("images/")) {
      return `${process.env.REACT_APP_S3_BASE_URL}${logo}`;
    }
    return null;
  }, [userData?.data?.user?.logo_img]);

  if (isCourseTrackLoading || isDigitalProductLoading || isInstructorLoading) {
    return (
      <p className="text-center">
        <Loader loading={true} />
      </p>
    );
  }

  const courseAndTrackData = courseTrackData?.data || [];
  const digitalProducts = digitalProductData?.data || [];

  const getEmbedUrl = (url) => {
    if (!url) return null;

    const isPlaylist = url.includes("list=");
    const playlistMatch = url.match(/[?&]list=([a-zA-Z0-9_-]+)/);
    const videoMatch = url.match(/(?:v=|\.be\/|\/embed\/)([a-zA-Z0-9_-]{11})/);

    if (isPlaylist && playlistMatch) {
      return `https://www.youtube.com/embed/videoseries?list=${playlistMatch[1]}`;
    }

    if (videoMatch) {
      return `https://www.youtube.com/embed/${videoMatch[1]}`;
    }

    return null;
  };

  const embedUrl = getEmbedUrl(userData?.data?.user?.creator_obj?.youtube);

  return (
    <>
      <Header customLogo={logoPreview} />
      <div className="min-vh-100" style={{ padding: "150px 0 90px 0" }}>
        {/* Hero Section */}
        <section
          className="position-relative"
          style={{
            backgroundColor: "#6f42c1",
            height: "250px",
          }}
        >
          {/* Background Letter */}
          {bannerPreview ? (
            <img
              src={bannerPreview}
              alt="Banner"
              className="position-absolute w-100 h-100"
              style={{
                objectFit: "cover",
                top: 0,
                left: 0,
              }}
            />
          ) : (
            <div
              className="text-white fw-bold position-absolute w-100 text-center"
              style={{
                fontSize: "10rem",
                opacity: 0.15,
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
              }}
            >
              {userData?.data?.user?.first_name?.charAt(0)}
            </div>
          )}

          {/* Tagline */}
          <div
            className="position-absolute text-white fw-bold text-end"
            style={{
              bottom: "18px",
              right: "20px",
              fontSize: "1.2rem",
            }}
          >
            {/* Mobile: wrap (with narrow width) */}
            <span
              className="d-block d-sm-none"
              style={{
                maxWidth: "150px",
                whiteSpace: "normal",
                display: "inline-block",
              }}
            >
              {userData?.data?.user?.tagline || t("bannerHeadline")}
            </span>

            {/* Tablet & Desktop: single line, full width */}
            <span
              className="d-none d-sm-block"
              style={{ whiteSpace: "nowrap" }}
            >
              {userData?.data?.user?.tagline || t("bannerHeadline")}
            </span>
          </div>
        </section>

        {/* Profile Section */}
        <div
          className="container position-relative"
          style={{ marginTop: "-70px" }}
        >
          <div className="row align-items-center g-4">
            {/* Profile Section */}
            <div className="col-12 col-md-3 text-center">
              <img
                src={
                  userData?.data?.user?.picture || images.profile.placeholder
                }
                alt="Profile"
                className="img-fluid rounded-circle"
                style={{
                  width: "140px",
                  height: "140px",
                  objectFit: "cover",
                }}
              />

              <h5 className="fw-bold mt-3 text-break">
                {userData?.data?.user?.first_name}{" "}
                {userData?.data?.user?.last_name}
              </h5>

              <p
                onClick={() => navigate("/profile/edit")}
                style={{
                  cursor: "pointer",
                  color: "var(--primaryColor)",
                  fontWeight: "600",
                  textDecorationColor: "var(--primaryColor)",
                  marginTop: "8px",
                  transition: "color 0.3s",
                }}
                onMouseEnter={(e) => (e.target.style.color = "#bb86fc")}
                onMouseLeave={(e) =>
                  (e.target.style.color = "var(--primaryColor)")
                }
              >
                {t("editLink")}
              </p>
            </div>

            {/* Stats Section */}
            <div className="col-12 col-md-9">
              <div className="row g-3 justify-content-center text-center">
                {/* Posts */}
                <div className="col-12 col-sm-4">
                  <div
                    className="h-100 py-3 d-flex gap-2 align-items-end justify-content-center site-btn"
                    style={{
                      cursor: "pointer",
                      // backgroundColor: "var(--primaryColor)",
                    }}
                    onClick={() =>
                      navigate("/community", {
                        state: userId ? { userId } : undefined,
                      })
                    }
                  >
                    <h5 className="fw-bold mb-0">
                      {userData?.data?.user?.total_posts}
                    </h5>
                    <span>{t("post")}</span>
                  </div>
                </div>

                {/* Reviews */}
                <div className="col-12 col-sm-4">
                  <div className="h-100 py-3 d-flex gap-1 align-items-end justify-content-center site-btn">
                    <h5 className="fw-bold mb-0">
                      {userData?.data?.user?.total_reviews}
                    </h5>

                    <div className="d-flex gap-1 mb-1">
                      {Array.from({ length: 5 }, (_, i) => {
                        const rating = userData?.data?.avg_rating || 0;
                        if (rating >= i + 1) {
                          return (
                            <i key={i} className="fas fa-star yellow-text" />
                          );
                        } else if (rating >= i + 0.5) {
                          return (
                            <i
                              key={i}
                              className="fas fa-star-half-alt yellow-text"
                            />
                          );
                        } else {
                          return (
                            <i key={i} className="far fa-star yellow-text" />
                          );
                        }
                      })}
                    </div>
                  </div>
                </div>

                {/* Courses */}
                <div className="col-12 col-sm-4">
                  <div className="h-100 py-3 d-flex gap-2 align-items-end justify-content-center site-btn">
                    <h5 className="fw-bold mb-0">
                      {userData?.data?.user?.total_courses}
                    </h5>
                    <span>{t("totalProduct")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="container py-5">
          <p>{userData?.data?.user?.bio}</p>

          {/* Social Links */}
          <div className="d-flex gap-3 mt-3">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.instagram}
            >
              <i className="fab fa-instagram fa-lg text-light"></i>
            </a>
            <a target="_blank" rel="noopener noreferrer" href={userData?.data?.user?.creator_obj?.tiktok}>
              <i className="fab fa-tiktok fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.youtube}
            >
              <i className="fab fa-youtube fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.website}
            >
              <i className="fas fa-globe fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.facebook}
            >
              <i className="fab fa-facebook fa-lg text-light"></i>
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={userData?.data?.user?.creator_obj?.linkedin}
            >
              <i className="fab fa-linkedin fa-lg text-light"></i>
            </a>
          </div>
        </div>

        {/* Courses & Tracks */}
        <div className="container py-4">
          <h3 className="fw-bold mb-4 text-center">{t("courseTitle")}</h3>
          {courseAndTrackData && courseAndTrackData.length > 0 ? (
            courseAndTrackData.map((item) => (
              <section className="classes-for-you">
                <div className="container">
                  <div className="row mb-1">
                    <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                      <h4 className="text-uppercase mb-1">{item?.name}</h4>
                      <p className="mb-0 m-purple-text">
                        {item?.count} classes
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <OwlCarousel
                      className="category-carousel"
                      options={{
                        margin: 10,
                        loop: false,
                        nav: true,
                        dots: false,
                        lazyLoad: true,
                        autoplay: true,
                        autoplayTimeout: 5000,
                        autoplayHoverPause: true,
                        smartSpeed: 500,
                        navText: [
                          '<i class="fas fa-angle-left"></i>',
                          '<i class="fas fa-angle-right"></i>',
                        ],
                        responsive: {
                          0: { items: 2, stagePadding: 0, autoplay: false },
                          576: {
                            items: 2,
                            stagePadding: 20,
                            autoplay: faBedPulse,
                          },
                          768: {
                            items: 3,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          992: {
                            items: 4,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          1200: {
                            items: 5,
                            stagePadding: 0,
                            autoplay: true,
                          },
                        },
                      }}
                    >
                      {item?.courses?.map((item) => (
                        <div className="">
                          <CourseCard data={item} />
                        </div>
                      ))}
                    </OwlCarousel>
                  </div>
                </div>
              </section>
            ))
          ) : (
            <div
              className="text-center py-4 rounded-2"
              style={{ backgroundColor: "var(--grayColor)" }}
            >
              <p className="text-light fw-bold mb-0 p-md">{t("notCourse")}</p>
            </div>
          )}
        </div>

        {/* My Digital Products */}
        <div className="container py-4">
          <h3 className="fw-bold mb-4 text-center">{t("productTitle")}</h3>
          {digitalProducts && digitalProducts.length > 0 ? (
            digitalProducts.map((item) => (
              <section className="classes-for-you">
                <div className="container">
                  <div className="row mb-1">
                    <div className="col-12 d-flex align-items-end gap-2 flex-wrap">
                      <h4 className="text-uppercase mb-1">{item?.name}</h4>
                      <p className="mb-0 m-purple-text">
                        {item?.count} classes
                      </p>
                    </div>
                  </div>
                  <div className="row">
                    <OwlCarousel
                      className="category-carousel"
                      options={{
                        margin: 10,
                        loop: false,
                        nav: true,
                        dots: false,
                        lazyLoad: true,
                        autoplay: true,
                        autoplayTimeout: 5000,
                        autoplayHoverPause: true,
                        smartSpeed: 500,
                        navText: [
                          '<i class="fas fa-angle-left"></i>',
                          '<i class="fas fa-angle-right"></i>',
                        ],
                        responsive: {
                          0: { items: 2, stagePadding: 0, autoplay: false },
                          576: {
                            items: 2,
                            stagePadding: 20,
                            autoplay: faBedPulse,
                          },
                          768: {
                            items: 3,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          992: {
                            items: 4,
                            stagePadding: 0,
                            autoplay: true,
                          },
                          1200: {
                            items: 5,
                            stagePadding: 0,
                            autoplay: true,
                          },
                        },
                      }}
                    >
                      {item?.courses?.map((item) => (
                        <div className="">
                          <CourseCard data={item} />
                        </div>
                      ))}
                    </OwlCarousel>
                  </div>
                </div>
              </section>
            ))
          ) : (
            <div
              className="text-center py-4 rounded-2"
              style={{ backgroundColor: "var(--grayColor)" }}
            >
              <p className="text-light fw-bold mb-0 p-md">{t("notProduct")}</p>
            </div>
          )}
        </div>

        {/* Success Stories & Work With Me */}
        <div className="container py-4">
          <div className="row align-items-center">
            {/* Success Stories */}
            <div className="col-lg-6 col-md-12 mb-4 text-center">
              <h3 className="fw-bold mb-4 text-uppercase">
                {t("successStory")}
              </h3>

              {/* Responsive YouTube Embed */}
              {embedUrl ? (
                <div className="ratio ratio-16x9 shadow">
                  <iframe
                    src={embedUrl}
                    title="Success Story Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ borderRadius: "12px" }}
                  ></iframe>
                </div>
              ) : (
                <>
                  <p className="text-center text-secondary fw-medium">
                    {t("noSuccessStory_before")} <strong>YouTube</strong>{" "}
                    {t("or")} <strong>TikTok</strong>{" "}
                    {t("noSuccessStory_after")}
                  </p>
                </>
              )}
            </div>

            {/* Work With Me */}
            <div className="col-lg-6 col-md-12 mb-4 text-center">
              <h3 className="fw-bold mb-4 text-uppercase">{t("workWith")}</h3>
              <div className="d-flex flex-column align-items-center gap-3">
                {userData?.data?.user?.creator_obj?.work_with_me_links?.map(
                  (link) => {
                    const url = link.url?.toLowerCase() || "";

                    const getIconForUrl = (url) => {
                      if (/zoom\.us/.test(url)) return "fas fa-video";
                      if (/meet\.google\.com/.test(url)) return "fab fa-google";
                      if (/eventbrite/.test(url)) return "fas fa-calendar";
                      if (/classpass/i.test(url)) return "fas fa-users";
                      if (/mindbodyonline/i.test(url)) return "fas fa-dumbbell";
                      if (/calendly/i.test(url)) return "fas fa-calendar-alt";
                      if (/acuityscheduling/i.test(url))
                        return "fas fa-calendar-check";
                      if (/whatsapp/i.test(url)) return "fab fa-whatsapp";
                      if (
                        /^mailto:/.test(url) ||
                        /^[\w.-]+@[\w.-]+\.\w{2,}$/.test(url)
                      )
                        return "fas fa-envelope";
                      if (/shopify/i.test(url)) return "fas fa-shopping-bag";
                      if (/youtube\.com/.test(url)) return "fab fa-youtube";
                      if (/instagram\.com/.test(url)) return "fab fa-instagram";
                      if (/tiktok\.com/.test(url)) return "fab fa-tiktok";
                      if (/^https?:\/\//.test(url)) return "fas fa-globe";
                      return "fas fa-link";
                    };

                    return (
                      <a
                        key={link._id}
                        href={
                          url.startsWith("http") || url.startsWith("mailto:")
                            ? link.url
                            : `mailto:${link.url}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none text-dark"
                      >
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span
                            className="d-inline-flex justify-content-center align-items-center rounded-circle"
                            style={{
                              width: "50px",
                              height: "50px",
                              background:
                                "linear-gradient(135deg, #ff0080, #7928ca)",
                              color: "white",
                              fontSize: "1.5rem",
                            }}
                          >
                            <i className={getIconForUrl(url)}></i>
                          </span>
                          <span
                            className="fw-bold text-light"
                            style={{ fontSize: "1.2rem" }}
                          >
                            {link.label}
                          </span>
                        </div>
                      </a>
                    );
                  },
                )}
              </div>

              <p className="text-secondary fw-medium mt-3">
                <span className="fw-bold">“{t("workWith")}”</span>{" "}
                {t("noWorkWithMe")}
              </p>
            </div>
          </div>
        </div>

        <div className="container py-4 text-center">
          <p
            className="text-secondary fw-medium mt-3 mx-auto"
            style={{ maxWidth: "500px" }}
          >
            {t("offeredServices")}. <strong>Traineego</strong>{" "}
            {t("notResponsible")}
          </p>
        </div>

        <FitnessSection />
      </div>
      <Footer />
    </>
  );
};

export default Profile;
